import { isTest, isTestnet } from '@bfe/by-env';
import { Select, Switch, Tooltip } from '@bfe/moly';
import { HelpIcon } from '@bfe/moly-icons';
import { RTLSkipStart } from '@bybit/rtl-plugin';
import { RTLSkipEnd } from '@bybit/rtl-plugin';
import { message } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import ComplianceElement from '@/components/ComplianceElement';
import BySecurity from '@/components/NewSecurity';
import { useErrorMsg } from '@/hooks/useErrorMsg';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useI18nContext } from '@/i18n/I18n';
import { getRiskStrategyApi } from '@/services/security';
import {
  getNoticeList,
  setNotificationLang,
  switchNotifyByNotice,
  switchNotifyByUser,
} from '@/services/setting';
import { UserInfo } from '@/services/types/common';
import { INoticeItem } from '@/services/types/settings';
import { useStore } from '@/store/useStore';
import { useUserStore } from '@/store/useUser';
import {
  NOTICE_MODULE_NEED_SAFETY,
  NOTICE_MODULE_STATUS,
} from '@/utils/constants/setting';
import { $tracing } from '@/utils/tracing';

import { langList as langListOrigin } from './index';
import TgBind from './TgBind';

const CHANGES_TO_ACCOUNT_INFO_ID = isTest ? 826 : isTestnet ? 125 : 109;

const Notice = () => {
  const [$t, lang] = useI18nContext();
  const { mutateUserInfo: mutate, userInfo } = useStore();
  const { msgError } = useErrorMsg(mutate);
  const [riskToken, setRiskToken] = useState<string>('');
  const [showSecurity, setShowSecurity] = useState(false);
  const currentNotice = useRef<{
    status: boolean;
    item: INoticeItem;
    index: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [noticeSwitchList, setNoticeSwitchList] = useState<INoticeItem[]>([]);
  const [closedModuleArr, setClosedModuleArr] = useState<boolean[]>([]);

  const { complianceConfig } = useUserStore();

  const langList = useMemo(() => {
    let langList = langListOrigin['COMMON'];
    try {
      langList = JSON.parse(complianceConfig?.noticeLanguages?.ext);
    } catch (error) {
      langList = langListOrigin['COMMON'];
    }
    return langList;
  }, [complianceConfig]);

  const updateUserByMutate = (obj: UserInfo) => {
    mutate((profile: any) => {
      if (profile?.result) profile.result = obj;
      return profile;
    });
  };
  // 获取通知列表
  const getNoticeSwitchList = () => {
    getNoticeList().then((res) => {
      const result = res as INoticeItem[];
      if (result) {
        setNoticeSwitchList(result.filter((item) => item.enable === 2));
      }
    });
  };
  // 设置邮箱短信接收语言
  const langChange = (lang: any) => {
    setNotificationLang(lang)
      .then(() => {
        const obj = {
          ...userInfo,
          lang,
        } as UserInfo;
        updateUserByMutate(obj);
        // 上报埋点
        $tracing.addEvent({
          t: 'action',
          c: 15107,
          m: `language:${lang}`,
        });
        if (import.meta.env.PROD) {
          window?.BybitUniFrame?.webPush?.bindWebPush?.();
        }
        message.success({
          content: $t('SUccess'),
        });
      })
      .catch((error) => {
        msgError(error);
      });
  };

  const noticeSwitchSuccess = () => {
    const { index, status, item } = currentNotice.current || {};

    // 上报埋点
    $tracing.addEvent({
      t: 'action',
      c: 15108,
      m: `moduleId:${item?.id}-status:${status}`,
    });
    message.success({
      content: $t('securityWithdrawCoins.settingSuccess'),
    });

    setClosedModuleArr((closedModuleArr) => {
      if (
        typeof index !== 'undefined' &&
        typeof closedModuleArr[index] !== 'undefined'
      ) {
        closedModuleArr[index] = status as boolean;
      }
      const obj = {
        ...userInfo,
        msg_disable_module_ids: closedModuleArr
          .map((moduleStatus, index) => {
            if (!moduleStatus && noticeSwitchList[index]) {
              return noticeSwitchList[index].id;
            }
            return '-1000';
          })
          .filter((id) => id !== '-1000')
          .join(','),
      } as UserInfo;
      updateUserByMutate(obj);
      return [...closedModuleArr];
    });
  };

  /**
   * 点击开关按钮
   * 判断是否需要唤起安全组件
   * risk_check === 1
   *  1. 操作开启订阅，直接进行开关操作；
   *  2. 操作关闭订阅，唤起安全组件
   *     安全组件返回 pass，则直接进入开关操作，否则
   *     进入安全组件验证
   * risk_check === 0 直接进行开关操作
   */
  const handleSwitchNotify = async (
    status: boolean,
    item: INoticeItem,
    index: number,
  ) => {
    currentNotice.current = {
      status,
      item,
      index,
    };
    setLoading(true);
    if (item.risk_check !== NOTICE_MODULE_NEED_SAFETY.NEED) {
      // 直接调用触达接口，进行开关操作
      switchNotifyByNoticeFn();
    } else {
      if (status) {
        // 开启时，不调用安全组件
        switchNotifyByUserFn('');
      } else {
        // 通过安全组件验证后，调用用户服务接口进行操作
        try {
          const rs: any = await getRiskStrategyApi({ sence: '30076' });
          if (rs.ret_code === 0) {
            const {
              result: { risk_token, risk_token_type },
            } = rs;
            if (risk_token_type === 'pass') {
              switchNotifyByUserFn(risk_token);
            } else if (risk_token) {
              setRiskToken(risk_token);
              setShowSecurity(true);
            }
          }
        } catch (error) {
          setLoading(false);
          message.error($t('securityWithdrawCoins.settingError'));
        }
      }
    }
  };

  /**
   * 通过触达接口设置模块的开启或关闭
   * 只对普通类型模块有效，目前判断条件为 risk_check === 0
   */
  const switchNotifyByNoticeFn = () => {
    const id = currentNotice.current?.item?.id || 0;
    switchNotifyByNotice(id)
      .then(noticeSwitchSuccess)
      .catch((error) => {
        msgError(error);
      })
      .finally(() => {
        console.log('switchNotify finally');
        setLoading(false);
      });
  };

  /**
   * 通过用户服务接口设置模块的开启或关闭
   * 只对需要走安全认证类型的模块有效，目前判断条件为 risk_check === 1
   */
  const switchNotifyByUserFn = (risk_token: string) => {
    const id = currentNotice.current?.item?.id || 0;
    const status = currentNotice.current?.status
      ? NOTICE_MODULE_STATUS.OPEN
      : NOTICE_MODULE_STATUS.CLOSE;
    switchNotifyByUser(id, status, risk_token)
      .then(noticeSwitchSuccess)
      .catch((error) => {
        msgError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 监听用户信息
  useEffect(() => {
    if (userInfo) {
      // 开始获取信息
      getNoticeSwitchList();
    }
  }, [userInfo, lang]);

  // 下线语言重置
  const langSet = (lang: string | undefined) => {
    if (lang === 'zh-CN') {
      return 'zh-MY';
    } else if (lang === 'fr-FR') {
      return 'en';
    } else if (lang === 'hi-IN') {
      return 'en';
    }
    if (langList.find((item) => item.value === lang)) {
      return lang;
    }
    return '';
  };

  useEffect(() => {
    const tempFlag =
      (userInfo && userInfo?.msg_disable_module_ids?.split?.(',')) || [];
    const finalFlagArr = [];
    const ids: number[] = [];
    noticeSwitchList.forEach((item: INoticeItem) => ids.push(item.id));
    for (const value of ids) {
      const tempValue = JSON.stringify(value);
      if (tempFlag.includes(tempValue)) {
        finalFlagArr.push(false);
      } else {
        finalFlagArr.push(true);
      }
    }
    setClosedModuleArr(finalFlagArr);
  }, [userInfo, noticeSwitchList]);

  return (
    <>
      <div className="notice-container">
        <h3 className="notice-setup-title">{$t('notice')}</h3>
        <div className="notice-setup-form">
          <div className="notice-setup-form-label">
            {$t('Notification Language')}
          </div>
          <div className="notice-setup-form-item-select">
            <Select
              width={480}
              defaultValue={langSet(userInfo?.lang)}
              virtual={false}
              placeholder={$t('Please Select')}
              key={langSet(userInfo?.lang)}
              onChange={langChange}
              showSearch
              options={langList.map((item) => {
                return {
                  label: item.label,
                  value: item.value,
                };
              })}
            ></Select>
          </div>
          {noticeSwitchList.length > 0 &&
            noticeSwitchList.map((item, index) => {
              return (
                <div
                  key={item.id}
                  className="notice-setup-form-item-switch-container"
                >
                  <div className="notice-setup-form-item-item">
                    <div className="switch-label">
                      <div className="notice-name">
                        {item.name}
                        {item.id === CHANGES_TO_ACCOUNT_INFO_ID && (
                          <>
                            <RTLSkipStart></RTLSkipStart>
                            <Tooltip
                              title={$t('changesToAccountInfoTips')}
                              hasDashedLine
                              placement="bottom"
                            >
                              <HelpIcon />
                            </Tooltip>
                            <RTLSkipEnd></RTLSkipEnd>
                          </>
                        )}
                      </div>
                      {item.desc ? (
                        <div className="notice-desc">{item.desc}</div>
                      ) : null}
                    </div>
                    <Switch
                      loading={
                        loading && item.id === currentNotice.current?.item.id
                      }
                      checked={closedModuleArr[index]}
                      key={String(closedModuleArr[index])}
                      onCheckedChange={(checked: boolean) =>
                        handleSwitchNotify(checked, item, index)
                      }
                    />
                  </div>
                </div>
              );
            })}
          {/* TG绑定及TG通知开关 */}
          <ComplianceElement config={complianceConfig?.Telegram}>
            <ComplianceElement config={complianceConfig?.TelegramNotice}>
              {!userInfo.is_submember ? <TgBind /> : null}
            </ComplianceElement>
          </ComplianceElement>
        </div>
      </div>
      {/*
       * 安全组件
       * 验证通过后，调用用户服务接口设置通知开关
       */}
      {showSecurity && (
        <BySecurity
          risk_token={riskToken}
          faceFailHandler={() => {
            setRiskToken('');
            setShowSecurity(false);
            setLoading(false);
          }}
          clearTokenHandler={() => {
            setRiskToken('');
            setShowSecurity(false);
            setLoading(false);
          }}
          callBack={(res: any) => {
            setRiskToken(res?.result.risk_token);
            switchNotifyByUserFn(res?.result.risk_token);
            setShowSecurity(false);
          }}
        />
      )}
    </>
  );
};
export default Notice;
